<template>
  <div v-if="loading">Loading...</div>
  <div v-else-if="errorMessage">
    Error loading dashboard: {{ errorMessage }}
  </div>
  <div v-else-if="showPlaceholder">
    <p>This is where you manage your bookings.</p>
    <p>
      When you book using your Bundle credits your bookings will show up here.
    </p>
  </div>
  <div v-else class="bookings-wrapper">
    <v-dialog />
    <div class="div-block-41">
      <h2>{{ customerName }}</h2>
      <div class="div-block-37">
        <h3 class="heading-3">Credits Remaining</h3>
        <div data-ms-member="credits" class="credit-count" style="opacity: 1;">
          {{ credits }}
        </div>
      </div>
    </div>

    <div id="new" class="bookings">
      <vue-good-table
        :columns="bookingColumns"
        :rows="bookingRows"
        max-height="400px"
        :fixed-header="true"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field == 'game_link'">
            <span v-if="props.row[props.column.field].indexOf('/') != -1">
              <a
                target="_blank"
                style="color: blue !important;"
                :href="props.row[props.column.field]"
                >Game Link</a
              >
              <a
                @click="copyGameLink(props.row[props.column.field])"
                style="margin-left: 20px;"
                class="link-block w-inline-block"
                ><div>Copy</div></a
              >
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </span>
          <span v-else-if="props.column.field == 'game_dashboard'">
            <span v-if="props.row[props.column.field]">
              <a
                :target="props.row[props.column.field].target"
                style="color: blue !important;"
                :href="props.row[props.column.field].link"
                >{{ props.row[props.column.field].title }}</a
              >
            </span>
          </span>
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
      </vue-good-table>
    </div>
  </div>
</template>
<script>
import DashboardService from '@/services/DashboardService.js'
import moment from 'moment'
import md5 from 'md5'
import 'vue-good-table/dist/vue-good-table.css'
import { VueGoodTable } from 'vue-good-table'

export default {
  props: {
    dashboard_code: { type: String, required: true }
  },
  components: {
    VueGoodTable
  },
  mounted() {
    document.querySelector('body').classList.add('display-scrollbar')
  },
  beforeDestroy() {
    document.querySelector('body').classList.remove('display-scrollbar')
  },
  created() {
    this.fetchDashboard(this.dashboard_code)
  },
  data() {
    return {
      bookingColumns: [
        {
          label: 'Date',
          field: 'event_start_date',
          type: 'date',
          dateInputFormat: 'yyyy-MM-dd',
          dateOutputFormat: 'do MMM yyyy'
        },
        {
          label: 'Booked by',
          field: 'booked_by',
          width: '100px'
        },
        {
          label: 'Event Name',
          field: 'event_name'
        },
        {
          label: 'Game Link',
          field: 'game_link',
          html: true
        },
        {
          label: 'Live Game Details',
          field: 'game_dashboard',
          html: true
        }
      ],
      errorMessage: '',
      showPlaceholder: false,
      dashboardData: undefined
    }
  },
  computed: {
    loading() {
      return (
        this.dashboardData === undefined &&
        this.errorMessage === '' &&
        this.showPlaceholder === false
      )
    },
    bookings() {
      return this.dashboardData?.bookings
    },
    customerName() {
      return this.dashboardData?.customerName
    },
    credits() {
      return this.dashboardData?.credits
    },
    bookingRows() {
      return this.bookings?.map(booking => {
        var eventName
        if (booking.data.event_name) {
          eventName = `${booking.data.event_name} (${booking.data.fareharbor_item})`
        } else {
          eventName = booking.data.fareharbor_item
        }

        var gameLink = ''
        if (booking.data.tas_game_code) {
          gameLink = `https://tasgame.web.app/game/${booking.data.tas_game_code}`
        } else if (booking.data.loquiz_game_id) {
          gameLink = 'Links sent via email'
        }

        var gameDashboardDetails
        if (booking.data.tas_game_code) {
          var linkTitle
          if (booking.data.tas_player_count === 1) {
            linkTitle = `1 player`
          } else if (booking.data.tas_player_count > 1) {
            linkTitle = `${booking.data.tas_player_count} players`
          } else {
            linkTitle = 'Live Game Details'
          }

          gameDashboardDetails = {
            link: `/game/${booking.data.tas_game_code}/${md5(
              booking.data.tas_game_code
            )}/dashboard`,
            title: linkTitle,
            target: '_self'
          }
        } else if (booking.data.loquiz_game_id) {
          gameDashboardDetails = {
            link: `https://results.loquiz.com/${booking.data.loquiz_game_id}:${booking.id}/media`,
            title: 'Game Media Page',
            target: '_blank'
          }
        }

        return {
          id: booking.id,
          booked_by: this.truncateStringToLength(
            booking.data.contact_email,
            20
          ),
          game: booking.data.fareharbor_item,
          event_name: eventName,
          game_code: booking.data.tas_game_code,
          event_start_date: moment(booking.data.event_start_date).format(
            'YYYY-MM-DD'
          ),
          game_link: gameLink,
          game_dashboard: gameDashboardDetails
        }
      })
    }
  },
  methods: {
    moment: function(date) {
      return moment(date)
    },
    momentDuration: function(ms) {
      return moment.duration(ms)
    },
    truncateStringToLength(string, length) {
      return string.length > length
        ? `${string.substring(0, length)}...`
        : string
    },
    copyGameLink(gameLink) {
      // https://stackoverflow.com/a/58734857/2347353
      const el = document.createElement('textarea')
      el.value = gameLink
      el.setAttribute('readonly', '')
      el.style.position = 'absolute'
      el.style.left = '-9999px'
      document.body.appendChild(el)
      const selected =
        document.getSelection().rangeCount > 0
          ? document.getSelection().getRangeAt(0)
          : false
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      if (selected) {
        document.getSelection().removeAllRanges()
        document.getSelection().addRange(selected)
      }

      this.$modal.show('dialog', {
        text: 'Game link copied to clipboard',
        buttons: [
          {
            title: 'Close',
            handler: () => {
              this.$modal.hide('dialog')
            }
          }
        ]
      })
    },
    fetchDashboard: function(key) {
      DashboardService.getDashboard(key)
        .then(response => {
          this.dashboardData = response.data
        })
        .catch(error => {
          console.log(
            'There was a problem fetching dashboard data: ' + error.message
          )

          if (error.response?.status === 404) {
            this.showPlaceholder = true
          } else {
            this.errorMessage =
              'There was a problem fetching dashboard data: ' + error.message
          }
        })
    }
  }
}
</script>
<style scoped>
@import url('https://fonts.googleapis.com/css?family=Poppins|Roboto');

* {
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
}

.container {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1200px;
  margin-right: auto;
  margin-left: auto;
  padding-right: 20px;
  padding-left: 20px;
  -webkit-box-align: end;
  -webkit-align-items: flex-end;
  -ms-flex-align: end;
  align-items: flex-end;
}

.section {
  padding-top: 45px;
  padding-bottom: 45px;
}

body {
  margin: 0;
  min-height: 100%;
  background-color: rgb(255, 255, 255);
  font-family: Poppins, sans-serif;
  color: rgb(51, 51, 51);
  font-size: 14px;
  line-height: 20px;
  margin-top: 0px;
  margin-right: 0px;
  margin-bottom: 0px;
  margin-left: 0px;
}

.body {
  font-family: Poppins, sans-serif;
}

html {
  font-family: sans-serif;
  -ms-text-size-adjust: 100%;
  -webkit-text-size-adjust: 100%;
  height: 100%;
  text-size-adjust: 100%;
}

.bookings-wrapper {
  padding: 20px;
  border-radius: 20px;
  background-color: rgb(248, 248, 248);
  padding-top: 20px;
  padding-right: 20px;
  padding-bottom: 20px;
  padding-left: 20px;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  border-bottom-right-radius: 20px;
  border-bottom-left-radius: 20px;
}

.div-block-41 {
  display: flex;
  -webkit-box-pack: justify;
  -webkit-justify-content: space-between;
  -ms-flex-pack: justify;
  justify-content: space-between;
}

.bookings {
  margin-top: 40px;
  padding: 20px;
  border-radius: 10px;
  background-color: rgb(255, 255, 255);
  box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 5px 0px inset;
  padding-top: 20px;
  padding-right: 20px;
  padding-bottom: 20px;
  padding-left: 20px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
}

h2 {
  font-weight: 700;
  margin-bottom: 10px;
}

h2 {
  margin-top: 20px;
  margin-bottom: 10px;
  color: rgb(253, 118, 36);
  font-size: 40px;
  line-height: 46px;
  font-weight: 600;
}

.div-block-37 {
  display: flex;
  margin-top: 13px;
  margin-bottom: 13px;
  padding: 12px 20px;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-box-pack: start;
  -webkit-justify-content: flex-start;
  -ms-flex-pack: start;
  justify-content: flex-start;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
  border-radius: 10px;
  background-color: rgb(255, 255, 255);
  text-align: center;
  padding-top: 12px;
  padding-right: 20px;
  padding-bottom: 12px;
  padding-left: 20px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
}

h3 {
  font-weight: 700;
  margin-bottom: 10px;
}

h3 {
  margin-top: 20px;
  margin-bottom: 10px;
  font-size: 24px;
  line-height: 30px;
  font-weight: 600;
}

.book-head {
  margin-top: 10px;
  margin-bottom: 20px;
  color: rgb(46, 24, 106);
}

.dash-table-head {
  display: flex;
  margin-bottom: 0px;
  padding: 0;
  -webkit-box-pack: justify;
  -webkit-justify-content: space-between;
  -ms-flex-pack: justify;
  justify-content: space-between;
  -webkit-box-align: stretch;
  -webkit-align-items: stretch;
  -ms-flex-align: stretch;
  align-items: stretch;
  border-radius: 4px;
  background-color: rgb(236, 236, 236);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  padding-top: 0px;
  padding-right: 0px;
  padding-bottom: 0px;
  padding-left: 0px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
}

.heading-3 {
  margin-top: 10px;
  color: rgb(46, 24, 106);
}

.credit-count {
  width: auto;
  margin-right: 18px;
  margin-left: 18px;
  padding: 12px 33px;
  border-radius: 10px;
  background-color: rgb(255, 255, 255);
  box-shadow: rgba(0, 0, 0, 0.23) 0px 1px 5px 1px inset;
  color: rgb(46, 24, 106);
  font-size: 24px;
  line-height: 24px;
  font-weight: 600;
  padding-top: 12px;
  padding-right: 33px;
  padding-bottom: 12px;
  padding-left: 33px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
}

[data-ms-member]:not(input) {
  opacity: 0;
}

.item {
  display: flex;
  width: 100%;
  padding: 11px 10px;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -webkit-flex-direction: column;
  -ms-flex-direction: column;
  flex-direction: column;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
  border-right: 1px solid #d1d1d1;
  text-align: center;
  padding-top: 11px;
  padding-right: 10px;
  padding-bottom: 11px;
  padding-left: 10px;
  border-right-width: 1px;
  border-right-style: solid;
  border-right-color: rgb(209, 209, 209);
}

.item.name {
  max-width: 25%;
}

.item.members {
  max-width: 35%;
}

.item.join {
  max-width: 20%;
  border-right-style: none;
}

.item.event-link {
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
}

.team-name {
  color: rgb(46, 24, 106);
  font-weight: 600;
}

a {
  background-color: transparent;
}

.w-inline-block {
  max-width: 100%;
  display: inline-block;
}

.link-block {
  margin-left: 5px;
  padding: 8px;
  border-radius: 5px;
  background-color: rgb(253, 118, 36);
  color: rgb(255, 255, 255);
  font-size: 12px;
  line-height: 12px;
  text-decoration: none;
  padding-top: 8px;
  padding-right: 8px;
  padding-bottom: 8px;
  padding-left: 8px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
  border-bottom-left-radius: 5px;
  text-decoration-line: none;
  text-decoration-thickness: initial;
  text-decoration-style: initial;
  text-decoration-color: initial;
  cursor: pointer;
}

a:active,
a:hover {
  outline: 0;
  outline-color: initial;
  outline-style: initial;
  outline-width: 0px;
  text-decoration: underline !important;
  text-decoration-line: underline;
}

.teams-players {
  display: flex;
  width: 20%;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
}

.pipe {
  margin-right: 10px;
  margin-left: 10px;
}

.bookings >>> .vgt-table {
  font-size: 12px !important;
}
</style>
